import { useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { FaTrash } from "react-icons/fa";
import MsTable from "../../../components/Widgets/Table";
import PageHeader from "components/PageHeader";
import { colType } from "../../../Types";
import {
  ArtistsBrief,
  ArtistsBriefSubmission,
} from "../../../api/artists-briefs";
import ArtistsBriefModal from "./ArtistsBriefModal";
import AddCardDialog from "./AddCardDialog";
import PreviewBriefsDialog from "./PreviewBriefsDialog";
import SubmissionModal from "../../../components/SubmissionModal";
import useArtistsBriefsQuery from "../hooks/useArtistsBriefsQuery";
import useArtistsBriefSubmissionsQuery from "../hooks/useArtistsBriefSubmissionsQuery";
import useSaveArtistsBriefsMutation, {
  getSaveBriefsErrorMessage,
} from "../hooks/useSaveArtistsBriefsMutation";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSBox from "../../system-design/DSBox";
import DSStack from "../../system-design/DSStack";
import DSAlert from "../../system-design/DSAlert";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { useQueryClient } from "@tanstack/react-query";
import { markBriefSubmissionsVisited } from "../../../components/hooks/useNewBriefSubmissions";

type ActiveTab = "cards" | "submissions";

function ArtistsBriefs() {
  const [tab, setTab] = useState<ActiveTab>("cards");
  const queryClient = useQueryClient();

  const handleTabChange = (value: ActiveTab) => {
    setTab(value);
    if (value === "submissions") {
      markBriefSubmissionsVisited();
      queryClient.invalidateQueries({ queryKey: ["newBriefSubmissions"] });
    }
  };
  const {
    data: briefs,
    isLoading: cardsLoading,
    isError: cardsError,
  } = useArtistsBriefsQuery();
  const {
    data: submissions,
    isLoading: submissionsLoading,
    isError: submissionsError,
  } = useArtistsBriefSubmissionsQuery();
  const {
    mutate: saveBriefs,
    isError: saveError,
    error: saveErrorObj,
  } = useSaveArtistsBriefsMutation();

  const [showAddCard, setShowAddCard] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [cardToDelete, setCardToDelete] = useState("");

  const onIndexChange = (id: string, value: string) => {
    if (!briefs) return;
    const parsed = Number(value.trim());
    if (value.trim() === "" || !Number.isInteger(parsed)) return;
    const currentIndex = briefs.findIndex((brief) => brief.id === id);
    if (currentIndex === -1) return;
    const newIndex = Math.min(Math.max(parsed - 1, 0), briefs.length - 1);
    if (newIndex === currentIndex) return;
    const reordered = [...briefs];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(newIndex, 0, moved);
    saveBriefs(reordered);
  };

  const deleteCard = (id: string) => {
    if (!briefs) return;
    saveBriefs(briefs.filter((brief) => brief.id !== id));
  };

  const cardColumns: Array<colType> = [
    { label: "index", field: "index", sort: false },
    { label: "image", field: "image", sort: false },
    { label: "title", field: "title", sort: true },
    { label: "labels", field: "labels", sort: false },
    { label: "active", field: "active", sort: true },
    { size: 32, noHeadline: true, sort: false, label: "", field: "delete" },
  ];

  const cardRows = (brief: ArtistsBrief) => {
    const position = briefs ? briefs.findIndex((b) => b.id === brief.id) : 0;
    return {
      index: (
        <input
          key={`${brief.id}-${position}`}
          type="number"
          min={1}
          max={briefs?.length ?? 1}
          defaultValue={position + 1}
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => onIndexChange(brief.id, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          style={{ width: "60px" }}
        />
      ),
      image: brief.imageUrl ? (
        <img
          width={64}
          height={64}
          src={brief.imageUrl}
          alt={brief.title}
          style={{ objectFit: "cover", borderRadius: 8 }}
        />
      ) : (
        <DSTypography sx={{ textAlign: "center" }}>No image</DSTypography>
      ),
      title: brief.title,
      labels: (
        <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {brief.labels?.map((label) => (
            <DSChip key={label} label={label} size="small" variant="outlined" />
          ))}
        </span>
      ),
      active: (
        <DSTypography
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ color: brief.active ? "green" : "red", fontWeight: "bold" }}
        >
          {brief.active ? "Active" : "Inactive"}
        </DSTypography>
      ),
      delete: (
        <FaTrash
          style={{ margin: "8px", cursor: "pointer" }}
          title="Delete card"
          onClick={(e) => {
            e.stopPropagation();
            setCardToDelete(brief.id);
          }}
        />
      ),
    };
  };

  const submissionColumns: Array<colType> = [
    { label: "date", field: "date", sort: true },
    { label: "briefs", field: "selectedBriefTitles", sort: false },
    { label: "name", field: "fullName", sort: true },
    { label: "email", field: "email", sort: true },
    { label: "portfolio", field: "portfolioUrl", sort: false },
    { label: "message", field: "message", sort: false },
  ];

  const submissionRows = (submission: ArtistsBriefSubmission) => ({
    date: submission.createdAt
      ? new Date(submission.createdAt).toLocaleString()
      : "",
    selectedBriefTitles: (submission.selectedBriefTitles || []).join(", "),
    fullName: submission.fullName,
    email: submission.email,
    portfolioUrl: submission.portfolioUrl ? (
      <div
        style={{
          width: "100%",
          overflowWrap: "anywhere",
          textAlign: "center",
        }}
      >
        <a
          href={submission.portfolioUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {submission.portfolioUrl}
        </a>
      </div>
    ) : (
      ""
    ),
    message: submission.message ? (
      <div
        style={{
          width: "100%",
          maxHeight: 84,
          overflowY: "auto",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          textAlign: "left",
        }}
      >
        {submission.message}
      </div>
    ) : (
      ""
    ),
  });

  return (
    <>
      <PageHeader
        overline="Content"
        title="Artists Briefs"
        subtitle="Manage the cards shown on the Artist Briefs page and review submissions."
        actions={
          tab === "cards" ? (
            <DSStack direction="row" spacing={1}>
              <DSSecondaryButton onClick={() => setShowPreview(true)}>
                Preview
              </DSSecondaryButton>
              <DSPrimaryButton onClick={() => setShowAddCard(true)}>
                Add Card
              </DSPrimaryButton>
            </DSStack>
          ) : undefined
        }
      />

      <Tabs
        value={tab}
        onChange={(_, value: ActiveTab) => handleTabChange(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="Cards" value="cards" />
        <Tab label="Submissions" value="submissions" />
      </Tabs>

      {saveError && (
        <DSAlert severity="error" sx={{ mb: 2 }}>
          {getSaveBriefsErrorMessage(saveErrorObj)}
        </DSAlert>
      )}

      {tab === "cards" &&
        (cardsLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <DSCircularProgress />
          </DSBox>
        ) : cardsError ? (
          <DSAlert severity="error">
            Failed to load cards. Please try again.
          </DSAlert>
        ) : briefs ? (
          <MsTable
            id="ArtistsBriefsCards"
            data={briefs}
            cols={cardColumns}
            rowBuild={cardRows}
            title="Cards"
            search={(row: ArtistsBrief, val: string) =>
              row.title.toLowerCase().includes(val.toLowerCase())
            }
            modal={ArtistsBriefModal}
          />
        ) : null)}

      {tab === "submissions" &&
        (submissionsLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <DSCircularProgress />
          </DSBox>
        ) : submissionsError ? (
          <DSAlert severity="error">
            Failed to load submissions. Please try again.
          </DSAlert>
        ) : submissions ? (
          <MsTable
            id="ArtistsBriefsSubmissions"
            data={submissions}
            cols={submissionColumns}
            rowBuild={submissionRows}
            title="Submissions"
            modal={SubmissionModal}
            search={(row: ArtistsBriefSubmission, val: string) =>
              `${row.fullName} ${row.email}`
                .toLowerCase()
                .includes(val.toLowerCase())
            }
          />
        ) : null)}

      {cardToDelete && (
        <DSDialog open onClose={() => setCardToDelete("")}>
          <DSDialogTitle>Confirm Deletion</DSDialogTitle>
          <DSDialogContent>
            Are you sure you want to delete this card?
          </DSDialogContent>
          <DSDialogActions>
            <DSSecondaryButton onClick={() => setCardToDelete("")}>
              Cancel
            </DSSecondaryButton>
            <DSErrorButton
              onClick={() => {
                deleteCard(cardToDelete);
                setCardToDelete("");
              }}
            >
              Delete
            </DSErrorButton>
          </DSDialogActions>
        </DSDialog>
      )}

      {showAddCard && (
        <DSDialog
          open
          maxWidth="sm"
          fullWidth
          onClose={() => setShowAddCard(false)}
        >
          <AddCardDialog
            existingBriefs={briefs || []}
            onClose={() => setShowAddCard(false)}
          />
        </DSDialog>
      )}

      {showPreview && (
        <DSDialog
          open
          maxWidth="lg"
          fullWidth
          onClose={() => setShowPreview(false)}
        >
          <PreviewBriefsDialog
            briefs={briefs || []}
            onClose={() => setShowPreview(false)}
          />
        </DSDialog>
      )}
    </>
  );
}

export default ArtistsBriefs;
